import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { ChevronRight, Play } from "lucide-react";
import { getImageUrl } from "@/utils/imageUrl";

// Helper to extract embed URL from various video platforms
function getEmbedUrl(url) {
  if (!url) return null;
  
  // YouTube
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) {
    return { type: 'iframe', url: `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&mute=1&loop=1&playlist=${ytMatch[1]}&controls=0` };
  }
  
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return { type: 'iframe', url: `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&muted=1&loop=1&background=1` };
  }
  
  // ScreenPal (formerly Screencast-O-Matic)
  const screenpalMatch = url.match(/go\.screenpal\.com\/watch\/([a-zA-Z0-9]+)/);
  if (screenpalMatch) {
    return { type: 'iframe', url: `https://go.screenpal.com/player/${screenpalMatch[1]}?autoplay=1&mute=1` };
  }
  
  // Loom
  const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loomMatch) {
    return { type: 'iframe', url: `https://www.loom.com/embed/${loomMatch[1]}?autoplay=1&mute=1` };
  }
  
  // Google Drive
  const gdriveMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (gdriveMatch) {
    return { type: 'iframe', url: `https://drive.google.com/file/d/${gdriveMatch[1]}/preview` };
  }
  
  // Direct video URL (MP4, WebM, etc.)
  if (url.match(/\.(mp4|webm|ogg)(\?|$)/i)) {
    return { type: 'video', url: url };
  }
  
  // Fallback - try as direct iframe embed
  return { type: 'iframe', url: url };
}

export default function ShowcaseCategoryCard({ category }) {
  const showVideo = category.media_type === "video" && category.video;
  const embedData = showVideo ? getEmbedUrl(category.video) : null;

  return (
    <Link to={`/showcase/${category.slug || category.id}`} data-testid={`showcase-category-${category.slug}`}>
      <Card className="group overflow-hidden cursor-pointer hover:border-primary/50 hover:shadow-lg transition-all duration-300 h-full">
        <div className="relative h-40 overflow-hidden bg-gray-100">
          {showVideo && embedData ? (
            // Video display
            embedData.type === 'video' ? (
              <video 
                src={embedData.url}
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-contain"
              />
            ) : (
              <iframe
                src={embedData.url}
                className="w-full h-full object-contain border-0"
                frameBorder="0"
                allow="autoplay; encrypted-media"
                allowFullScreen
                title={category.name}
              />
            )
          ) : category.image ? (
            // Image display
            <img 
              src={getImageUrl(category.image)} 
              alt={category.name}
              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          
          {/* Fallback placeholder */}
          <div 
            className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/20 to-secondary/20 ${(showVideo && embedData) || category.image ? 'hidden' : 'flex'}`}
          >
            <span className="text-4xl font-bold text-primary/30">{category.name.charAt(0)}</span>
          </div>
          
          {/* Video indicator */}
          {showVideo && (
            <div className="absolute top-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
              <Play className="h-3 w-3" fill="currentColor" />
              Video
            </div>
          )}
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-lg group-hover:text-primary transition-colors" style={{ fontFamily: 'Manrope' }}>
              {category.name}
            </h3>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
          </div>
        </div>
      </Card>
    </Link>
  );
}
